import { featureFlags } from '@seen/core/featureFlags'
import { TypographyDetail, useIsLg } from '@seen/ui'
import { isEmpty } from 'lodash-es'
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useSearchParams } from 'react-router'
import { VirtualizedList } from '#src/components/core/VirtualizedList.tsx'
import { Helmet } from '#src/components/Helmet.tsx'
import { InboxDetailLayout } from '#src/components/layouts/InboxDetailLayout.tsx'
import { InboxArchiveAdvanceContext } from '#src/contexts/InboxArchiveAdvanceContext.ts'
import { InboxDeferredArchiveContext } from '#src/contexts/InboxDeferredArchiveContext.ts'
import { InboxLiveSyncContext } from '#src/contexts/InboxLiveSyncContext.ts'
import { InboxSiblingsContext } from '#src/contexts/InboxSiblingsContext.ts'
import { INBOX_CATEGORIES } from '#src/hooks/inbox/inboxCategories.ts'
import { useInboxBackgroundFetch } from '#src/hooks/inbox/useInboxBackgroundFetch.ts'
import {
  itemMatchesCategory,
  useInboxCategoryFilter,
} from '#src/hooks/inbox/useInboxCategoryFilter.ts'
import { useInboxCategoryStore } from '#src/hooks/inbox/useInboxCategoryStore.ts'
import { useInboxDedupedCounts } from '#src/hooks/inbox/useInboxDedupedCounts.ts'
import {
  type InboxListItem,
  useInboxDedupedItems,
  useInboxGroupKeyResolver,
} from '#src/hooks/inbox/useInboxDedupedItems.ts'
import { useInboxFeedReconcile } from '#src/hooks/inbox/useInboxFeedReconcile.ts'
import { useInboxSidebar } from '#src/hooks/inbox/useInboxSidebar.ts'
import { useLegacyCommentDedupeKeys } from '#src/hooks/inbox/useLegacyCommentDedupeKeys.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useKnockFeedClient } from '#src/hooks/useKnockFeedClient.ts'
import { useNotificationItems } from '#src/hooks/useNotificationItems.ts'
import inboxZeroImport from '#src/pages/inbox/inbox-zero.png'
import { InboxCategoryBar } from '#src/pages/inbox/InboxCategoryBar.tsx'
import { InboxDetailPanel } from '#src/pages/inbox/InboxDetailPanel.tsx'
import { InboxItemRow } from '#src/pages/inbox/InboxItemRow.tsx'
import { InboxMobileFilters } from '#src/pages/inbox/InboxMobileFilters.tsx'
import { InboxNavigation } from '#src/pages/inbox/InboxNavigation.tsx'
import { InboxReadFilterTabs } from '#src/pages/inbox/InboxReadFilter.tsx'
import { InboxReconcilePill } from '#src/pages/inbox/InboxReconcilePill.tsx'
import '@knocklabs/react/dist/index.css'
import { InboxViewAs } from '#src/pages/inbox/InboxViewAs.tsx'

/* Next.js returns StaticImageData (object with .src), other bundlers return a string */
const inboxZeroUrl =
  typeof inboxZeroImport === 'string'
    ? inboxZeroImport
    : (inboxZeroImport as { src: string }).src

const INBOX_ROW_HEIGHT = 62

export function InboxPage(): ReactNode {
  const inboxFeedClient = useKnockFeedClient()
  const {
    items: inboxItems,
    loading,
    hasMore,
    isLoadingMore,
    fetchNextPage,
  } = useNotificationItems()

  useInboxBackgroundFetch({
    hasMore,
    isLoadingMore,
    fetchNextPage,
  })
  const { selectedCategory, setSelectedCategory, readFilter, setReadFilter } =
    useInboxCategoryStore()

  const readFilteredItems = useMemo(() => {
    if (readFilter === 'unread') {
      return inboxItems.filter((i) => !i.read_at)
    }
    if (readFilter === 'read') {
      return inboxItems.filter((i) => i.read_at)
    }
    return inboxItems
  }, [inboxItems, readFilter])

  const {
    categoryMap,
    statItemIds,
    overdueItemIds,
    completedTaskItemIds,
    isResolving,
  } = useInboxCategoryFilter(readFilteredItems)

  /* Dedup the read-filtered list ONCE. Both the category counts and the
     displayed rows derive from this single deduped set, filtered by category
     through the same predicate, so the list and the counts can never disagree.
     getSiblings spans all categories in the read-filtered feed, so archive /
     read cascades reach same-resource siblings outside the active category. */
  const legacyDedupeKeys = useLegacyCommentDedupeKeys(readFilteredItems)
  const inboxTrustEnabled = useFeatureFlag(featureFlags.inboxTrust)
  const resolveGroupKey = useInboxGroupKeyResolver({
    legacyDedupeKeys,
    threadGroupingEnabled: inboxTrustEnabled,
  })

  const { items: allDedupedItems, getSiblings } = useInboxDedupedItems(
    readFilteredItems,
    resolveGroupKey
  )

  const dedupedCounts = useInboxDedupedCounts(
    allDedupedItems,
    categoryMap,
    statItemIds,
    overdueItemIds,
    completedTaskItemIds
  )

  const [searchParams] = useSearchParams()
  const selectedKey = searchParams.get('selected')

  /* The rendered rows: the same deduped set, minus completed/cancelled-task
     notifications (which the counts also exclude), filtered to the selected
     category. The pinned-item carve-out keeps the currently-open item in the
     list even after its task is completed (so ghost styling + j/k stay valid). */
  const dedupedItems = useMemo(() => {
    const cats = { categoryMap, statItemIds, overdueItemIds }
    return allDedupedItems.filter(
      (li) =>
        (!completedTaskItemIds.has(li.item.id) || li.item.id === selectedKey) &&
        itemMatchesCategory(li.item.id, selectedCategory, cats)
    )
  }, [
    allDedupedItems,
    selectedCategory,
    categoryMap,
    statItemIds,
    overdueItemIds,
    completedTaskItemIds,
    selectedKey,
  ])

  const isDesktop = useIsLg()

  const {
    selectItem,
    effectiveSelectedItem,
    effectiveKey,
    sidebarOpen,
    advanceSelectionAwayFrom,
    archiveOrDefer,
  } = useInboxSidebar({
    items: dedupedItems,
    getSiblings,
    selectedCategory,
    resolveGroupKey,
    feedClient: inboxFeedClient,
  })

  // Only the row actually on screen is protected; when the panel is closed
  // nothing is exempt from removal.
  const protectedItemId = sidebarOpen
    ? effectiveSelectedItem?.item.id
    : undefined

  /* The coordinator that applies removals lives beside the feeds, not here, so
     the sidebar count keeps up while the user is anywhere else in the app. All
     this route owns is which row is currently exempt. */
  const { setProtectedItemId, markStoreReplaced } =
    useContext(InboxLiveSyncContext)

  useEffect(() => {
    setProtectedItemId(protectedItemId)
  }, [protectedItemId, setProtectedItemId])

  useEffect(
    () => () => {
      setProtectedItemId(undefined)
    },
    [setProtectedItemId]
  )

  const { pendingRefresh, reconcile } = useInboxFeedReconcile({
    feedClient: inboxFeedClient,
    isEngaged: sidebarOpen,
    liveRemovalEnabled: inboxTrustEnabled,
    onReplaced: markStoreReplaced,
    protectedItemId,
  })

  /*
   * Keep a ref to selection state so the renderItem callback can stay stable
   * across selection changes — selection updates don't recreate all N rows.
   */
  const selectionRef = useRef({
    selectItem,
    effectiveKey,
    sidebarOpen,
    isDesktop,
  })
  selectionRef.current = { selectItem, effectiveKey, sidebarOpen, isDesktop }

  const renderItem = useCallback(
    (index: number) => {
      const listItem = dedupedItems[index]
      if (!listItem) {
        return null
      }
      const {
        selectItem: sel,
        effectiveKey: key,
        sidebarOpen: open,
        isDesktop: desktop,
      } = selectionRef.current
      return (
        <InboxListRow
          key={listItem.key}
          listItem={listItem}
          selectItem={desktop ? sel : undefined}
          selectedItemKey={desktop ? key : undefined}
          sidebarOpen={open}
        />
      )
    },
    [dedupedItems]
  )

  if (loading && isEmpty(inboxItems)) {
    return null
  }

  if (isEmpty(inboxItems) && !hasMore) {
    return (
      <div className="relative isolate h-full">
        <Helmet title="Inbox" />
        <div className="absolute top-0 left-0 -z-10 h-full w-full bg-cover bg-center">
          <img
            src={inboxZeroUrl}
            alt=""
            className="relative h-full w-full object-cover object-center"
          />
          <div
            className="absolute top-0 left-0 h-full w-full"
            style={{
              background:
                'linear-gradient(rgba(0, 0, 0, 0.55) 0px, rgba(0, 0, 0, 0.055) 264px, transparent 324px), linear-gradient(to top, rgb(0, 0, 0) 0px, rgba(0, 0, 0, 0.1) 264px, transparent 324px)', // Gradient from superhuman.com's inbox zero state.
            }}
          />
        </div>
        <div className="dark flex h-full flex-col justify-between p-pagex-default lg:p-pagex-lg">
          <div className="flex justify-between gap-2">
            <InboxNavigation className="text-foreground" />
            <InboxViewAs />
          </div>
          <TypographyDetail className="text-[rgba(255,255,255,0.8)]">
            You&rsquo;ve hit Inbox Zero!
          </TypographyDetail>
        </div>
      </div>
    )
  }

  const categoryConfig = INBOX_CATEGORIES.find((c) => c.id === selectedCategory)
  const categoryEmptyMessage = categoryConfig?.emptyMessage ?? ''
  const filterNoun = categoryConfig?.filterNoun ?? 'items'

  let emptyMessage = categoryEmptyMessage
  if (readFilter !== 'all' && isEmpty(readFilteredItems)) {
    emptyMessage = `No ${readFilter} items`
  } else if (readFilter !== 'all' && isEmpty(dedupedItems)) {
    emptyMessage = `No ${readFilter} ${filterNoun}`
  }

  const listContent = (
    <div className="flex size-full flex-col">
      <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 border-b p-pagex-default lg:p-pagex-lg">
        <div className="flex min-w-0 items-center gap-2">
          <InboxNavigation />
        </div>
        {/* Only ever visible with the coordinator off: nothing is pruning
            behind the open panel then, so the refresh is offered manually. */}
        <div className="justify-self-center">
          <InboxReconcilePill visible={pendingRefresh} onClick={reconcile} />
        </div>
        <div className="min-w-0 justify-self-end">
          <InboxViewAs />
        </div>
      </div>
      <div className="shrink-0 border-b">
        {/* Mobile: two compact dropdown buttons */}
        <div className="lg:hidden">
          <InboxMobileFilters
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            counts={dedupedCounts}
            readFilter={readFilter}
            onReadFilterChange={setReadFilter}
            isLoading={hasMore || isResolving}
          />
        </div>
        {/* Desktop: scrollable category bar + pinned read filter */}
        <div className="hidden items-center lg:flex">
          <div className="min-w-0 flex-1">
            <InboxCategoryBar
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
              counts={dedupedCounts}
              isLoading={hasMore || isResolving}
            />
          </div>
          <div className="shrink-0 border-l py-1.5 pr-pagex-lg pl-2">
            <InboxReadFilterTabs
              readFilter={readFilter}
              onReadFilterChange={setReadFilter}
            />
          </div>
        </div>
      </div>
      {!isEmpty(inboxItems) && isEmpty(dedupedItems) ? (
        <div className="flex flex-1 items-center justify-center">
          <span className="text-sm text-muted-foreground">{emptyMessage}</span>
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          <VirtualizedList
            renderItem={renderItem}
            itemCount={dedupedItems.length}
            estimateSize={() => INBOX_ROW_HEIGHT}
            scrollRestorationId="inbox-page"
            onLoadMore={fetchNextPage}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
          />
        </div>
      )}
    </div>
  )

  if (!isDesktop) {
    return (
      <InboxDeferredArchiveContext.Provider value={archiveOrDefer}>
        <InboxSiblingsContext.Provider value={getSiblings}>
          <InboxArchiveAdvanceContext.Provider value={advanceSelectionAwayFrom}>
            <Helmet title="Inbox" />
            {listContent}
          </InboxArchiveAdvanceContext.Provider>
        </InboxSiblingsContext.Provider>
      </InboxDeferredArchiveContext.Provider>
    )
  }

  return (
    <InboxDeferredArchiveContext.Provider value={archiveOrDefer}>
      <InboxSiblingsContext.Provider value={getSiblings}>
        <InboxArchiveAdvanceContext.Provider value={advanceSelectionAwayFrom}>
          <Helmet title="Inbox" />
          <InboxDetailLayout
            autoSaveId="inbox-detail-layout"
            list={listContent}
            detail={
              effectiveSelectedItem ? (
                <InboxDetailPanel
                  item={effectiveSelectedItem.item}
                  onClose={() => selectItem(undefined)}
                />
              ) : null
            }
          />
        </InboxArchiveAdvanceContext.Provider>
      </InboxSiblingsContext.Provider>
    </InboxDeferredArchiveContext.Provider>
  )
}

function InboxListRow({
  listItem,
  selectItem,
  selectedItemKey,
  sidebarOpen,
}: {
  listItem: InboxListItem
  selectItem?: (key: string | undefined) => void
  selectedItemKey?: string | null
  sidebarOpen?: boolean
}): ReactNode {
  return (
    <InboxItemRow
      item={listItem.item.data}
      notificationItem={listItem.item}
      onSelect={selectItem ? () => selectItem(listItem.key) : undefined}
      isSelected={selectedItemKey === listItem.key}
      sidebarOpen={sidebarOpen}
    />
  )
}
